import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronLeft, ChevronRight, X, Download, Loader2, FileText, AlertTriangle, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Lightbox de prévisualisation rapide des documents d'un membre.
 * Permet la navigation ← → entre les documents sans quitter le tableau.
 *
 * Props :
 *  - open: boolean
 *  - onClose: () => void
 *  - documents: Array<{id, original_filename, type_document, ...}>
 *  - initialIndex: number — index du document à afficher en premier
 *  - membreId: string — pour construire l'URL de download (utilisé par défaut)
 *  - token: string — bearer token
 *  - typeConfig: mapping {type_document: {label, icon, badge}}
 *  - urlBuilder: (doc, opts) => {url, headers?, useTokenQuery?: boolean} — override l'URL de fetch (optionnel)
 *  - filenameOf: (doc) => string — extrait le nom de fichier (optionnel, défaut doc.original_filename)
 *  - typeOf: (doc) => string — extrait la clé typeConfig (optionnel, défaut doc.type_document)
 */
export default function DocPreviewLightbox({
  open,
  onClose,
  documents = [],
  initialIndex = 0,
  membreId,
  token,
  typeConfig = {},
  urlBuilder,
  filenameOf,
  typeOf,
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const total = documents.length;
  const currentDoc = documents[currentIndex] || null;

  // Réinitialise l'index quand la lightbox s'ouvre
  useEffect(() => {
    if (open) setCurrentIndex(initialIndex);
  }, [open, initialIndex]);

  // Charge le blob du document courant
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    const fetchBlob = async () => {
      if (!open || !currentDoc) return;
      setLoading(true);
      setError(null);
      setBlobUrl(null);
      setContentType('');
      try {
        let fetchUrl;
        let fetchHeaders = {};
        if (urlBuilder) {
          const built = urlBuilder(currentDoc) || {};
          fetchUrl = built.url;
          fetchHeaders = built.headers || (token ? { Authorization: `Bearer ${token}` } : {});
        } else {
          if (!membreId || !token) return;
          fetchUrl = `${API}/api/admin/membres/${membreId}/documents/${currentDoc.id}/download?mode=view`;
          fetchHeaders = { Authorization: `Bearer ${token}` };
        }
        const res = await fetch(fetchUrl, { headers: fetchHeaders });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get('content-type') || '';
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setContentType(ct);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBlob();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, currentDoc, membreId, token, urlBuilder]);

  // Navigation clavier (← → Esc)
  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i > 0 ? i - 1 : total - 1));
  }, [total]);
  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i < total - 1 ? i + 1 : 0));
  }, [total]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, goPrev, goNext, onClose]);

  const handleDownload = async () => {
    if (!currentDoc || !blobUrl) return;
    try {
      const fname = filenameOf ? filenameOf(currentDoc) : currentDoc.original_filename;
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fname || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  if (!currentDoc) return null;

  const docFilename = filenameOf ? filenameOf(currentDoc) : currentDoc.original_filename;
  const docTypeKey = typeOf ? typeOf(currentDoc) : currentDoc.type_document;
  const conf = typeConfig[docTypeKey] || {};
  const isPdf = contentType.includes('pdf');
  const isImage = contentType.startsWith('image/');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-6xl w-[95vw] h-[92vh] p-0 overflow-hidden flex flex-col bg-gray-900 border-gray-700"
        data-testid="doc-preview-lightbox"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-700 bg-gradient-to-r from-gray-900 to-gray-800">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <FileText className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <p
                className="text-sm font-semibold text-white truncate"
                title={docFilename}
                data-testid="lightbox-filename"
              >
                {docFilename || 'Document sans nom'}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                {conf.label && (
                  <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-4 font-semibold', conf.badge || 'bg-slate-700 text-slate-200 border-slate-600')}>
                    {conf.label}
                  </Badge>
                )}
                <span className="text-[11px] text-gray-400">
                  Document <strong className="text-gray-200">{currentIndex + 1}</strong> / {total}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs gap-1.5 text-gray-200 hover:bg-gray-700 hover:text-white"
              onClick={handleDownload}
              disabled={!blobUrl}
              data-testid="lightbox-download-btn"
            >
              <Download className="h-3.5 w-3.5" /> Télécharger
            </Button>
            {blobUrl && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs gap-1.5 text-gray-200 hover:bg-gray-700 hover:text-white"
                onClick={() => window.open(blobUrl, '_blank')}
                data-testid="lightbox-newtab-btn"
                title="Ouvrir dans un nouvel onglet"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-gray-200 hover:bg-gray-700 hover:text-white"
              onClick={onClose}
              data-testid="lightbox-close-btn"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Body — viewer */}
        <div className="flex-1 relative bg-gray-800 overflow-hidden flex items-center justify-center">
          {/* Bouton précédent */}
          {total > 1 && (
            <button
              type="button"
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-gray-900/80 hover:bg-gray-900 backdrop-blur-sm border border-gray-600 hover:border-gray-500 flex items-center justify-center text-white shadow-lg transition-all hover:scale-105"
              data-testid="lightbox-prev-btn"
              aria-label="Document précédent"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          {/* Bouton suivant */}
          {total > 1 && (
            <button
              type="button"
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-gray-900/80 hover:bg-gray-900 backdrop-blur-sm border border-gray-600 hover:border-gray-500 flex items-center justify-center text-white shadow-lg transition-all hover:scale-105"
              data-testid="lightbox-next-btn"
              aria-label="Document suivant"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}

          {/* Contenu */}
          {loading ? (
            <div className="flex flex-col items-center gap-3 text-gray-300">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm">Chargement du document…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 text-red-300 max-w-md text-center px-6">
              <AlertTriangle className="h-10 w-10" />
              <p className="text-sm font-medium">Impossible de charger le document</p>
              <p className="text-xs text-red-400">{error}</p>
            </div>
          ) : isPdf ? (
            <iframe
              src={blobUrl}
              className="w-full h-full"
              title={currentDoc.original_filename || 'Document'}
              data-testid="lightbox-pdf-iframe"
            />
          ) : isImage ? (
            <img
              src={blobUrl}
              alt={currentDoc.original_filename || 'Document'}
              className="max-w-full max-h-full object-contain"
              data-testid="lightbox-image"
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-gray-300 max-w-md text-center px-6">
              <FileText className="h-10 w-10" />
              <p className="text-sm">Aperçu non disponible pour ce type de fichier.</p>
              <Button onClick={handleDownload} variant="secondary" size="sm" className="gap-1.5">
                <Download className="h-3.5 w-3.5" /> Télécharger pour ouvrir
              </Button>
            </div>
          )}
        </div>

        {/* Footer — pagination dots + raccourcis */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-700 bg-gray-900">
          <p className="text-[11px] text-gray-400 hidden sm:block">
            <span className="font-mono px-1.5 py-0.5 bg-gray-800 rounded border border-gray-700 mr-1">←</span>
            <span className="font-mono px-1.5 py-0.5 bg-gray-800 rounded border border-gray-700 mr-2">→</span>
            naviguer
            <span className="font-mono px-1.5 py-0.5 bg-gray-800 rounded border border-gray-700 mx-1 ml-3">Esc</span>
            fermer
          </p>
          {total > 1 && total <= 12 && (
            <div className="flex items-center gap-1">
              {documents.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setCurrentIndex(i)}
                  className={cn(
                    'h-1.5 rounded-full transition-all',
                    i === currentIndex ? 'w-6 bg-indigo-400' : 'w-1.5 bg-gray-600 hover:bg-gray-500',
                  )}
                  aria-label={`Aller au document ${i + 1}`}
                  data-testid={`lightbox-dot-${i}`}
                />
              ))}
            </div>
          )}
          {total > 12 && (
            <span className="text-[11px] text-gray-400 font-mono">{currentIndex + 1} / {total}</span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
